import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  HelpCircle,
  Brain,
  Clock,
  Heart,
  FileText,
  ChevronRight,
  ChevronDown,
  Shield,
  Eye,
  UserCheck,
  ClipboardList
} from 'lucide-react';
import AssessmentLayout from '../../components/assessment/AssessmentLayout';
import ChecklistItem from '../../components/assessment/ChecklistItem';

export default function AssessmentGuidance() {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['intro', 'communication', 'understanding', 'mental', 'history', 'emotional'])
  );

  const handleContinue = () => {
    navigate('/assessment/categories');
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const sections = [
    {
      id: 'intro',
      title: 'Før du møter innsatte',
      icon: UserCheck,
      content: [
        'Sjekk om noen i avdelingen kjenner den innsatte fra før',
        'Se etter relevant informasjon om den innsatte i Kompis'
      ]
    },
    {
      id: 'communication',
      title: 'Kommunikasjon med innsatte',
      icon: MessageSquare,
      content: [
        'Forklar tydelig hvorfor du kommer for å snakke med ham',
        'Påpek at samtalen er for å bli kjent og kunne tilby nødvendig hjelp',
        'Sikre både din egen og den innsattes trygghet ved å holde trygg avstand',
        'Forklar hvorfor trygg avstand holdes dersom det er nødvendig'
      ]
    },
    {
      id: 'understanding',
      title: 'Forståelse av situasjonen',
      icon: Eye,
      content: [
        'Forstår han selv hvorfor han er i en IU-celle eller trenger ekstra tilsyn?',
        'Stemmer hans forklaring overens med årsaken til hendelsen(e)?',
        'Unngå å diskutere rett og galt, men noter hans perspektiv'
      ]
    },
    {
      id: 'mental',
      title: 'Psykisk og fysisk tilstand',
      icon: Brain,
      content: [
        'Har han en klar forklaring på hva som skjedde?',
        'Virker han ruset? Bekrefter han dette?',
        'Virker han deprimert? Bekrefter han dette?',
        'Oppleves han som farlig eller voldelig overfor betjentene?'
      ]
    },
    {
      id: 'history',
      title: 'Historikk og tidligere adferd',
      icon: Clock,
      content: [
        'Har han en historikk med lignende hendelser i andre fengsler?',
        'Har han selv fortalt om tidligere episoder, enten i Halden fengsel eller andre steder?'
      ]
    },
    {
      id: 'emotional',
      title: 'Emosjonell tilstand og videre behov',
      icon: Heart,
      content: [
        'Er han avvisende eller har han mye på hjertet?',
        'Gråter han? Kan han forklare hvorfor?',
        'Angrer han på det som skjedde? Uttrykker han ønske om å ikke gjenta det?',
        'Vil han snakke med helseavdelingen?',
        'Er dette hans første gang i fengsel?',
        'Virker han til å være i en krisesituasjon?'
      ]
    }
  ];

  return (
    <AssessmentLayout
      title="Nødvendige avklaringer før du fortsetter"
      subtitle="Følg stegene for å sikre en trygg og effektiv prosess."
      backTo="/assessment/start"
      currentStep={2}
    >
      {/* Main content */}
      <div className="space-y-4 mb-6">
        {sections.map((section) => (
          <div
            key={section.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border dark:border-gray-700"
          >
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <section.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{section.title}</h3>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${
                  expandedSections.has(section.id) ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSections.has(section.id) && (
              <div className="px-4 pb-4 space-y-3">
                {section.content.map((text, index) => (
                  <ChecklistItem key={index} text={text} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Next steps */}
      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6 mb-6 border border-blue-100 dark:border-blue-800">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Videre tiltak</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <ChecklistItem text="Når du har fått en oversikt, registrer informasjonen inn videre i dette programmet" />
          <ChecklistItem text="Informer Helseavdelingen om hvilken Modul han har fått" />
          <ChecklistItem text="Få innspill fra helseavdelingen om tilnærming til den innsatte" />
          <ChecklistItem text="Samarbeid med helseavdelingen for videre oppfølging" />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          className="flex items-center gap-2 bg-green-600 dark:bg-green-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
        >
          Jeg har lest og forstått
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </AssessmentLayout>
  );
}
